/* iter107 — Hikvision fingerprint terminal (DS-K1T808MFWX) settings tab. */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Fingerprint, Copy, ArrowsClockwise, Play, LinkSimple, DownloadSimple, UploadSimple, FileCsv, Image as ImageIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';

const STATUS = {
  punched: ['Pointé', 'bg-emerald-100 text-emerald-700'], unmapped: ['Non associé', 'bg-amber-100 text-amber-700'],
  duplicate: ['Doublon', 'bg-slate-100 text-slate-600'], ignored: ['Ignoré', 'bg-slate-100 text-slate-600'],
};
const fmt = (iso) => iso ? new Date(iso).toLocaleString('fr-FR') : '—';

const HikvisionTab = () => {
  const [cfg, setCfg] = useState(null);
  const [maps, setMaps] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState('');
  const [simNo, setSimNo] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const nextY = (n) => { const d = new Date(); d.setFullYear(d.getFullYear() + n); return d.toISOString().slice(0, 10); };
  const [validFrom, setValidFrom] = useState(today);
  const [validTo, setValidTo] = useState(nextY(5));
  const [assignMissing, setAssignMissing] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [photo, setPhoto] = useState(null);
  const base = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    api.get('/hikvision/settings').then(r => setCfg(r.data)).catch(e => toast.error(e?.response?.data?.detail || 'Erreur'));
    api.get('/hikvision/mappings').then(r => setMaps(r.data.items)).catch(() => {});
    api.get('/hikvision/events', { params: { limit: 30 } }).then(r => setEvents(r.data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const update = async (body) => {
    try { const { data } = await api.put('/hikvision/settings', body); setCfg(c => ({ ...c, ...data })); toast.success('Paramètres enregistrés'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const saveMap = async (m, no) => {
    try { await api.put(`/hikvision/mappings/${m.kind}/${m.id}`, { employee_no: no }); toast.success(`N° ${no || '—'} enregistré pour ${m.name}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const simulate = async () => {
    try {
      const { data } = await api.post('/hikvision/simulate', { employee_no: simNo, attendance_status: 'checkIn' });
      data.status === 'punched' ? toast.success(`Pointage simulé : ${data.employee_name} (${data.punch_type})`) : toast.warning(`${STATUS[data.status]?.[0] || data.status} — ${data.reason || ''}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success('Copié'));

  const exportUsers = async (fmt = 'xlsx') => {
    setSyncing(true);
    try {
      const res = await api.get('/hikvision/users/export.xlsx', {
        params: { valid_from: validFrom, valid_to: validTo, assign_missing: assignMissing, format: fmt },
        responseType: 'blob',
      });
      const total = res.headers['x-export-total'];
      const assigned = res.headers['x-export-assigned'];
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([res.data]));
      link.download = `import_pointeuse_hikvision_${today}.${fmt}`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Fichier généré : ${total} employé(s)${Number(assigned) > 0 ? `, ${assigned} nouveau(x) N° attribué(s)` : ''}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur lors de la génération'); }
    finally { setSyncing(false); }
  };

  const importPunches = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/hikvision/punches/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(data);
      toast.success(`${data.created} pointage(s) importé(s) — ${data.duplicates} doublon(s) ignoré(s)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import impossible'); }
    finally { setImporting(false); }
  };

  const downloadImportPdf = async (reportId) => {
    try {
      const res = await api.get(`/hikvision/punches/import-report/${reportId}.pdf`, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      link.download = `recap_import_pointages.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur PDF'); }
  };

  if (!cfg) return <div className="text-sm text-[#94A3B8]">Chargement…</div>;
  const url = `${base}${cfg.webhook_path}`;
  const filtered = maps.filter(m => !q || m.name.toLowerCase().includes(q.toLowerCase()) || m.employee_no.includes(q));

  return (
    <div className="space-y-6" data-testid="hikvision-tab">
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><Fingerprint size={20} weight="duotone" className="text-[#0052FF]" /> Pointeuse biométrique Hikvision (DS-K1T808MFWX)</span>
            <label className="flex items-center gap-2 text-xs font-normal"><Switch checked={cfg.enabled} onCheckedChange={v => update({ enabled: v })} data-testid="hik-enabled" /> {cfg.enabled ? 'Réception activée' : 'Désactivée'}</label>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            {[['Événements reçus', cfg.stats.events], ['Pointages créés', cfg.stats.punched], ['Appareils', cfg.stats.devices.length], ['Dernier événement', fmt(cfg.stats.last_event_at)]].map(([l, v]) => (
              <div key={l} className="p-2 rounded-md border border-[#E2E8F0]"><div className="font-outfit font-bold text-[#0F172A]" data-testid={`hik-stat-${l.split(' ')[0].toLowerCase()}`}>{v}</div><div className="text-[11px] text-[#64748B]">{l}</div></div>
            ))}
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium text-[#64748B] flex items-center gap-1"><LinkSimple size={12} /> URL de réception (HTTP Listening) à saisir sur la pointeuse</div>
            <div className="flex gap-2">
              <Input readOnly value={url} className="font-mono text-xs" data-testid="hik-webhook-url" />
              <Button variant="outline" size="sm" onClick={() => copy(url)} data-testid="hik-copy-url"><Copy size={14} /></Button>
              <Button variant="outline" size="sm" onClick={() => { if (window.confirm('Régénérer le jeton ? La pointeuse devra être reconfigurée.')) update({ regenerate_token: true }); }} data-testid="hik-regen-token" title="Régénérer le jeton"><ArrowsClockwise size={14} /></Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-medium text-[#64748B]">Sens du pointage :</span>
            {[['device', 'Selon la touche Entrée/Sortie de la pointeuse (checkIn/checkOut)'], ['auto', 'Alternance automatique (1er = entrée, 2e = sortie)']].map(([k, l]) => (
              <label key={k} className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="radio" checked={cfg.punch_mode === k} onChange={() => update({ punch_mode: k })} data-testid={`hik-mode-${k}`} /> {l}</label>
            ))}
          </div>
          <details className="rounded-md border border-[#E2E8F0] p-3 text-xs text-[#475569]" data-testid="hik-guide">
            <summary className="cursor-pointer font-medium text-[#0F172A]">Guide de configuration de la pointeuse (5 étapes)</summary>
            <ol className="list-decimal pl-5 mt-2 space-y-1">
              <li>Connectez la pointeuse au réseau (Ethernet/Wi-Fi) avec accès Internet et réglez l'heure (NTP) dans <b>Système › Heure</b>.</li>
              <li>Dans le navigateur, ouvrez l'interface web de la pointeuse (http://IP-pointeuse), menu <b>Configuration › Réseau › Paramètres avancés › HTTP Listening</b> (ou via ISAPI : <code>PUT /ISAPI/Event/notification/httpHosts/1</code>).</li>
              <li>Renseignez : Protocole <b>HTTPS</b>, Adresse <b>{new URL(url).host}</b>, Port <b>443</b>, URL <b>{cfg.webhook_path}</b>, Format <b>JSON</b>, Authentification <b>Aucune</b>. Cliquez « Test » : la réponse doit être <code>200 OK</code>.</li>
              <li>Activez <b>Événement › Événement de contrôle d'accès › Notifier le centre de surveillance / HTTP</b> pour les événements « Authentification réussie ».</li>
              <li>Sur la pointeuse, créez les utilisateurs avec un <b>N° d'employé</b> et enregistrez leurs empreintes ; reportez ce numéro dans le tableau ci-dessous pour chaque membre du personnel.</li>
            </ol>
            <p className="mt-2">Vérification : utilisez « Simuler un pointage » ci-dessous, puis faites une vraie lecture d'empreinte et contrôlez le journal des événements.</p>
          </details>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={simNo} onChange={e => setSimNo(e.target.value)} placeholder="N° employé (ex. 1001)" className="w-44 h-8" data-testid="hik-sim-no" />
            <Button size="sm" variant="outline" onClick={simulate} disabled={!simNo.trim()} data-testid="hik-sim-btn"><Play size={14} className="mr-1" /> Simuler un pointage</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0] shadow-none" data-testid="hik-sync-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <DownloadSimple size={20} weight="duotone" className="text-[#0052FF]" /> Synchronisation des utilisateurs vers la pointeuse
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p className="text-xs text-[#64748B]">
            La pointeuse étant sur votre réseau local, générez ici un fichier d'import (Excel) à charger dans <b>iVMS-4200</b> ou l'interface web de la pointeuse.
            Chaque employé reçoit son <b>N° d'employé</b> (identique à celui de l'application) et sa <b>période de validité</b>.
          </p>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-[#64748B]">Valide à partir du</label>
              <Input type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} className="h-9 w-44" data-testid="hik-valid-from" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-[#64748B]">Valide jusqu'au</label>
              <Input type="date" value={validTo} onChange={e => setValidTo(e.target.value)} className="h-9 w-44" data-testid="hik-valid-to" />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer pb-2">
              <Switch checked={assignMissing} onCheckedChange={setAssignMissing} data-testid="hik-assign-missing" />
              Attribuer un N° aux employés qui n'en ont pas
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => exportUsers('xlsx')} disabled={syncing} data-testid="hik-export-users-btn" className="bg-[#0052FF] hover:bg-[#0043cc]">
              <DownloadSimple size={16} className="mr-1.5" /> {syncing ? 'Génération…' : 'Générer le fichier Excel'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportUsers('csv')} disabled={syncing} data-testid="hik-export-users-csv-btn">
              <FileCsv size={16} className="mr-1.5" /> Format CSV (ancien iVMS-4200)
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0] shadow-none" data-testid="hik-import-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <UploadSimple size={20} weight="duotone" className="text-[#0052FF]" /> Ré-importer les pointages (retour après coupure réseau)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-xs text-[#64748B]">
            En cas de coupure réseau, la pointeuse conserve les pointages en local. Exportez-les depuis <b>iVMS-4200</b> ou l'interface web
            (format <b>Excel ou CSV</b>, avec au minimum le <b>N° d'employé</b> et la <b>date/heure</b>), puis chargez le fichier ici.
            Les pointages déjà enregistrés sont automatiquement détectés et ignorés (aucun doublon).
          </p>
          <div>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={importPunches} className="hidden" id="hik-import-file" data-testid="hik-import-file" />
            <Button size="sm" variant="outline" disabled={importing} onClick={() => document.getElementById('hik-import-file').click()} data-testid="hik-import-btn">
              <UploadSimple size={16} className="mr-1.5" /> {importing ? 'Import en cours…' : 'Charger un fichier de pointages'}
            </Button>
          </div>
          {importResult && (
            <div className="rounded-md border border-[#E2E8F0] p-3 text-xs space-y-1" data-testid="hik-import-result">
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                <span><b className="text-emerald-600">{importResult.created}</b> importé(s)</span>
                <span><b className="text-slate-500">{importResult.duplicates}</b> doublon(s) ignoré(s)</span>
                <span><b className="text-amber-600">{importResult.unmapped}</b> non associé(s)</span>
                <span><b className="text-red-500">{importResult.errors}</b> erreur(s)</span>
                <span className="text-[#94A3B8]">{importResult.parsed}/{importResult.rows} lignes lues</span>
              </div>
              {importResult.unmapped_nos?.length > 0 && (
                <div className="text-amber-700">N° non associés : {importResult.unmapped_nos.join(', ')} — reliez-les dans le tableau ci-dessous.</div>
              )}
              {importResult.report_id && (
                <Button size="sm" variant="outline" className="mt-1" onClick={() => downloadImportPdf(importResult.report_id)} data-testid="hik-import-pdf-btn">
                  <FileCsv size={14} className="mr-1.5" /> Télécharger le récapitulatif PDF
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0] shadow-none" data-testid="hik-mappings-card">
        <CardHeader className="pb-2"><CardTitle className="font-outfit text-base flex items-center justify-between gap-2">Association N° employé pointeuse ↔ personnel
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…" className="h-8 w-48 font-normal" data-testid="hik-map-search" /></CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-md border border-[#E2E8F0] max-h-96 overflow-y-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Fonction</TableHead><TableHead className="w-48">N° employé (pointeuse)</TableHead></TableRow></TableHeader>
              <TableBody>
                {filtered.map(m => (
                  <TableRow key={`${m.kind}-${m.id}`} data-testid={`hik-map-row-${m.id}`}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-xs text-[#64748B]">{m.role}</TableCell>
                    <TableCell>
                      <Input defaultValue={m.employee_no} placeholder="—" className="h-8 font-mono" data-testid={`hik-map-input-${m.id}`}
                        onBlur={e => { const v = e.target.value.trim(); if (v !== m.employee_no) saveMap(m, v); }}
                        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0] shadow-none" data-testid="hik-events-card">
        <CardHeader className="pb-2"><CardTitle className="font-outfit text-base flex items-center justify-between">Derniers événements reçus
          <Button size="sm" variant="ghost" onClick={load} data-testid="hik-events-refresh"><ArrowsClockwise size={14} /></Button></CardTitle></CardHeader>
        <CardContent>
          {events.length === 0 && <div className="text-sm text-[#94A3B8]" data-testid="hik-events-empty">Aucun événement reçu pour le moment.</div>}
          {events.length > 0 && (
            <div className="rounded-md border border-[#E2E8F0] overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Reçu le</TableHead><TableHead>Photo</TableHead><TableHead>N° employé</TableHead><TableHead>Personnel</TableHead><TableHead>Appareil</TableHead><TableHead>Statut</TableHead><TableHead>Détail</TableHead></TableRow></TableHeader>
                <TableBody>
                  {events.map(e => (
                    <TableRow key={e.id} data-testid={`hik-event-${e.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmt(e.received_at)}</TableCell>
                      <TableCell>
                        {e.has_photo ? (
                          <img src={`${base}/api/hikvision/events/${e.id}/photo`} alt="pointage" loading="lazy"
                            className="h-10 w-10 rounded object-cover border border-[#E2E8F0] cursor-pointer hover:ring-2 hover:ring-[#0052FF] transition"
                            onClick={() => setPhoto(`${base}/api/hikvision/events/${e.id}/photo`)}
                            data-testid={`hik-event-photo-${e.id}`} />
                        ) : <ImageIcon size={18} className="text-[#CBD5E1]" />}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{e.employee_no || '—'}{e.name ? ` (${e.name})` : ''}</TableCell>
                      <TableCell className="text-sm">{e.employee_name || '—'}</TableCell>
                      <TableCell className="text-xs text-[#64748B]">{e.device_serial || '—'}{e.simulated ? ' (simulation)' : ''}</TableCell>
                      <TableCell><Badge className={`${STATUS[e.status]?.[1] || ''} hover:bg-inherit`}>{STATUS[e.status]?.[0] || e.status}{e.punch_type ? ` · ${e.punch_type === 'in' ? 'Entrée' : 'Sortie'}` : ''}</Badge></TableCell>
                      <TableCell className="text-xs text-[#64748B]">{e.reason || e.attendance_status || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {photo && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setPhoto(null)} data-testid="hik-photo-modal">
          <div className="bg-white rounded-lg p-2 max-w-lg" onClick={e => e.stopPropagation()}>
            <img src={photo} alt="Photo de pointage" className="max-h-[70vh] rounded" />
            <div className="text-center mt-1">
              <Button size="sm" variant="ghost" onClick={() => setPhoto(null)} data-testid="hik-photo-close">Fermer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HikvisionTab;
